import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, {
  TELEGRAM_WEBHOOK_URL,
  type SystemStats,
  type OwnerUser,
  type OwnerUserDetail,
  type OwnerSchool,
  type OwnerSchoolDetail,
  type Subscription,
  type Plan,
  type OwnerDiscount,
} from '../services/api';

type Tab = 'dashboard' | 'users' | 'schools' | 'subscriptions' | 'plans';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'dashboard', label: '📊 Dashboard' },
  { key: 'users', label: '👥 Users' },
  { key: 'schools', label: '🏫 Schools' },
  { key: 'subscriptions', label: '💳 Подписки' },
  { key: 'plans', label: '📋 Тарифы' },
];

const ROLE_STYLES: Record<string, string> = {
  owner: 'bg-purple-50 text-purple-600 border-purple-100',
  admin: 'bg-blue-50 text-blue-600 border-blue-100',
  tester: 'bg-amber-50 text-amber-600 border-amber-100',
  user: 'bg-gray-50 text-gray-600 border-gray-100',
  owner_school: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  teacher: 'bg-orange-50 text-orange-600 border-orange-100',
};

const SUB_STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-50 text-green-600 border-green-200',
  trial: 'bg-blue-50 text-blue-600 border-blue-200',
  cancelled: 'bg-amber-50 text-amber-600 border-amber-200',
  expired: 'bg-gray-50 text-gray-500 border-gray-200',
  past_due: 'bg-red-50 text-red-600 border-red-200',
  pending: 'bg-purple-50 text-purple-600 border-purple-200',
};

const FEATURE_LABELS: Record<string, string> = {
  school_team: 'Команда/школа',
  advanced_stats: 'Расширенная статистика',
  export: 'Экспорт данных',
  payments_advanced: 'Продвинутые платежи',
  automation: 'Автоматизация',
};

const LIMIT_LABELS: Record<string, string> = {
  max_students: 'Учеников',
  max_teachers: 'Учителей',
  max_groups: 'Групп',
  max_courses: 'Курсов',
};

function RoleBadge({ role, label }: { role: string; label?: string }) {
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border ${ROLE_STYLES[role] || 'bg-gray-50 text-gray-600 border-gray-100'}`}>
      {label || role}
    </span>
  );
}

function StatCard({ icon, label, value, tone }: { icon: string; label: string; value: number | string; tone?: string }) {
  return (
    <div className="bg-[var(--tg-theme-section-bg-color)] rounded-2xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-[10px] text-[var(--tg-theme-hint-color)]">{label}</span>
      </div>
      <span className={`text-2xl font-bold ${tone || 'text-[var(--tg-theme-text-color)]'}`}>{value}</span>
    </div>
  );
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso.length > 10 ? iso : iso + 'T00:00:00');
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso.length > 10 ? iso : iso + 'T00:00:00');
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function EmptyState({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="tg-card flex flex-col items-center py-8 text-center">
      <span className="text-4xl mb-3">{icon}</span>
      <p className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-1">{title}</p>
      <p className="text-xs text-[var(--tg-theme-hint-color)] max-w-[260px]">{text}</p>
    </div>
  );
}

export default function SystemPage() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const [tab, setTab] = useState<Tab>('dashboard');

  useEffect(() => {
    if (!permissions.canOpenOwnerPanel) {
      navigate('/', { replace: true });
    }
  }, [permissions.canOpenOwnerPanel, navigate]);

  if (!permissions.canOpenOwnerPanel) return null;

  return (
    <div className="p-4 space-y-4 animate-fade-in pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Назад
        </button>
        <div className="px-3 py-1 rounded-full bg-purple-50 text-purple-600 text-xs font-medium border border-purple-100">
          👑 Owner
        </div>
      </div>

      <h2 className="text-lg font-semibold text-[var(--tg-theme-text-color)]">
        EduPulse — панель владельца
      </h2>

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
              tab === t.key
                ? 'bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] border-transparent'
                : 'bg-[var(--tg-theme-section-bg-color)] text-[var(--tg-theme-hint-color)] border-[var(--tg-theme-section-separator-color)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'schools' && <SchoolsTab />}
      {tab === 'subscriptions' && <SubscriptionsTab />}
      {tab === 'plans' && <PlansTab />}
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────────

function DashboardTab() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setStats(await api.getSystemStats());
      setError(null);
    } catch (e) {
      console.error('Failed to load owner stats:', e);
      setError('Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !stats) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="tg-card flex flex-col items-center py-6">
        <span className="text-3xl mb-2">😔</span>
        <p className="text-sm text-[var(--tg-theme-hint-color)] mb-3">{error}</p>
        <button onClick={load} className="tg-button text-sm">Попробовать снова</button>
      </div>
    );
  }

  if (!stats) return null;
  const subs = stats.subscriptions;

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon="👥" label="Пользователей" value={stats.users_total} />
        <StatCard icon="🟢" label="Активных" value={stats.users_active} tone="text-green-500" />
        <StatCard icon="🆕" label="Новых за 7 дней" value={stats.users_new_7d} tone="text-blue-500" />
        <StatCard icon="🏫" label="Школ" value={stats.schools_total} />
        <StatCard icon="🧑‍🏫" label="Учителей" value={stats.teachers_total} />
        <StatCard icon="🛡️" label="Админов" value={stats.admins_total} />
        <StatCard icon="👤" label="Solo-преподавателей" value={stats.solo_users} />
        <StatCard icon="🎓" label="Владельцев школ" value={stats.owner_school_total} />
      </div>

      {/* Roles breakdown */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>🎭</span> По ролям
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(stats.users_by_role).map(([role, count]) => (
            <span key={role} className={`px-2 py-1 rounded-full text-[10px] font-medium border ${ROLE_STYLES[role] || 'bg-gray-50 text-gray-600 border-gray-100'}`}>
              {role}: {count}
            </span>
          ))}
        </div>
      </div>

      {/* Subscriptions — SaaS analytics (ЭТАП 7) */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>💳</span> Подписки
        </h3>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            ['Активные', subs.active], ['Trial', subs.trial], ['Истёкшие', subs.expired],
            ['Отменённые', subs.cancelled], ['Ожидают', subs.pending], ['Новые (30д)', subs.new_30d],
            ['MRR, $', `$${subs.mrr}`], ['Отмены (30д)', subs.cancelled_30d], ['Churn, %', `${subs.churn_rate}%`],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-xl bg-[var(--tg-theme-secondary-bg-color)] p-2">
              <div className="text-lg font-bold text-[var(--tg-theme-text-color)]">{value}</div>
              <div className="text-[9px] text-[var(--tg-theme-hint-color)]">{label}</div>
            </div>
          ))}
        </div>

        {/* Free → Paid conversion + by-plan breakdown */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between rounded-xl bg-[var(--tg-theme-secondary-bg-color)] px-3 py-2">
            <span className="text-xs text-[var(--tg-theme-hint-color)]">Конверсия Free → Paid</span>
            <span className="text-sm font-bold text-[var(--tg-theme-text-color)]">{subs.conversion_free_to_paid}%</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(subs.by_plan).map(([plan, count]) => (
              <span key={plan} className={`px-2 py-1 rounded-full text-[10px] font-medium border capitalize ${
                plan === 'free' ? 'bg-gray-50 text-gray-600 border-gray-200'
                  : plan === 'pro' ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : plan === 'school' ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  : 'bg-purple-50 text-purple-700 border-purple-200'
              }`}>
                {plan}: {count}
              </span>
            ))}
          </div>
        </div>

        {/* Monthly series — MRR / new / cancelled */}
        {Array.isArray(subs.monthly) && subs.monthly.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] font-medium text-[var(--tg-theme-hint-color)] mb-1.5">
              По месяцам (6 мес.)
            </div>
            <div className="space-y-1">
              {subs.monthly.map(row => {
                const maxMrr = Math.max(1, ...subs.monthly.map(r => r.mrr));
                return (
                  <div key={row.month} className="flex items-center gap-2 text-[10px]">
                    <span className="w-14 shrink-0 text-[var(--tg-theme-hint-color)] font-mono">{row.month}</span>
                    <div className="flex-1 h-4 rounded bg-[var(--tg-theme-secondary-bg-color)] overflow-hidden">
                      <div
                        className="h-full bg-[var(--tg-theme-button-color)] rounded transition-all"
                        style={{ width: `${Math.max(2, (row.mrr / maxMrr) * 100)}%` }}
                      />
                    </div>
                    <span className="w-14 shrink-0 text-right text-[var(--tg-theme-text-color)] font-mono">${row.mrr}</span>
                    <span className="w-16 shrink-0 text-right text-[var(--tg-theme-hint-color)]">
                      +{row.new} / −{row.cancelled}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {subs.total === 0 && (
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-2">
            Подписки появятся после подключения платных тарифов.
          </p>
        )}
      </div>

      {/* System status */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>🖥️</span> Системный статус
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--tg-theme-hint-color)]">PostgreSQL (Neon)</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stats.postgres ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
              {stats.postgres ? '🟢 Online' : '🟡 Off'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--tg-theme-hint-color)]">Google Sheets</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stats.google_sheets ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
              {stats.google_sheets ? '🟢 Online' : '🟡 Off'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--tg-theme-hint-color)]">API</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">🟢 {stats.api_status}</span>
          </div>
        </div>
      </div>

      {/* Recent registrations */}
      {stats.recent_users.length > 0 && (
        <div className="tg-card">
          <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
            <span>📋</span> Последние регистрации
          </h3>
          <div className="space-y-2">
            {stats.recent_users.map(u => (
              <div key={u.id || u.username} className="flex items-center gap-3 py-1.5">
                <div className="w-7 h-7 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-[9px] font-bold shrink-0">
                  {u.first_name?.[0] || '?'}{u.last_name?.[0] || ''}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium truncate block">{u.first_name} {u.last_name}</span>
                  {u.username && <span className="text-[9px] text-[var(--tg-theme-hint-color)]">@{u.username}</span>}
                </div>
                <RoleBadge role={u.role} />
                <span className="text-[9px] text-[var(--tg-theme-hint-color)] shrink-0">{fmtDate(u.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Users ─────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = ['user', 'teacher', 'admin', 'owner_school', 'tester', 'owner'];

function UsersTab() {
  const [users, setUsers] = useState<OwnerUser[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<OwnerUserDetail | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (q: string) => {
    try {
      setLoading(true);
      const data = await api.getOwnerUsers(q);
      setUsers(data.users);
      setError(null);
    } catch (e) {
      console.error('Failed to load users:', e);
      setError('Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const apply = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    try {
      await fn();
      await load(search);
      if (selected && selected.id === id) {
        setSelected(await api.getOwnerUser(id));
      }
    } catch (e) {
      alert('Не удалось выполнить действие: ' + (e instanceof Error ? e.message : e));
    } finally {
      setBusyId(null);
    }
  };

  const openDetail = async (id: string) => {
    try {
      setSelected(await api.getOwnerUser(id));
    } catch (e) {
      alert('Не удалось открыть профиль');
    }
  };

  return (
    <>
      <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-[var(--tg-theme-secondary-bg-color)]">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') load(search); }}
          placeholder="🔍 Поиск: имя, @username, telegram id"
          className="w-full rounded-xl px-3 py-2 text-sm bg-[var(--tg-theme-section-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]"
        />
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {loading && users.length === 0 && (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="skeleton h-14 rounded-2xl" />)}</div>
      )}

      <div className="space-y-2">
        {users.map(u => (
          <div key={u.id} className="tg-card p-3" onClick={() => openDetail(u.id)}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-xs font-bold shrink-0">
                {u.first_name?.[0] || '?'}{u.last_name?.[0] || ''}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium truncate">{u.first_name} {u.last_name}</span>
                  {!u.is_active && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100 shrink-0">🔒 блок</span>}
                </div>
                <div className="text-[10px] text-[var(--tg-theme-hint-color)] truncate">
                  {u.username ? `@${u.username} · ` : ''}tg {u.telegram_id} · {u.school_name || 'solo'}
                </div>
              </div>
              <RoleBadge role={u.role} label={u.role_label} />
            </div>
          </div>
        ))}
        {!loading && users.length === 0 && (
          <div className="tg-card py-6 text-center text-sm text-[var(--tg-theme-hint-color)]">Никого не найдено</div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <UserDetailModal
          user={selected}
          busy={busyId === selected.id}
          onClose={() => setSelected(null)}
          onBlock={() => apply(selected.id, () => api.setOwnerUserActive(selected.id, !selected.is_active))}
          onRoleChange={role => apply(selected.id, () => api.setOwnerUserRole(selected.id, role))}
        />
      )}
    </>
  );
}

function UserDetailModal({ user, busy, onClose, onBlock, onRoleChange }: {
  user: OwnerUserDetail;
  busy: boolean;
  onClose: () => void;
  onBlock: () => void;
  onRoleChange: (role: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-[var(--tg-theme-bg-color)] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-base font-bold">
              {user.first_name?.[0] || '?'}{user.last_name?.[0] || ''}
            </div>
            <div>
              <div className="font-semibold text-[var(--tg-theme-text-color)]">{user.first_name} {user.last_name}</div>
              <div className="text-xs text-[var(--tg-theme-hint-color)]">{user.username ? `@${user.username}` : 'без username'} · tg {user.telegram_id}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--tg-theme-hint-color)] text-xl leading-none">✕</button>
        </div>

        <div className="space-y-3 text-sm">
          <div className="tg-card p-3">
            <div className="grid grid-cols-2 gap-y-2 text-xs">
              <span className="text-[var(--tg-theme-hint-color)]">Регистрация</span><span>{fmtDateTime(user.created_at)}</span>
              <span className="text-[var(--tg-theme-hint-color)]">Последний вход</span><span>{fmtDateTime(user.last_login_at)}</span>
              <span className="text-[var(--tg-theme-hint-color)]">Часовой пояс</span><span>{user.timezone || '—'}</span>
              <span className="text-[var(--tg-theme-hint-color)]">Школа</span><span>{user.school_name || '—'}</span>
            </div>
          </div>

          {user.school && (
            <div className="tg-card p-3">
              <div className="text-xs font-semibold mb-1">🏫 {user.school.name}</div>
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-[var(--tg-theme-hint-color)]">Владелец</span><span>{user.school.owner_name || '—'}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Создана</span><span>{fmtDate(user.school.created_at)}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Timezone</span><span>{user.school.timezone || '—'}</span>
              </div>
            </div>
          )}

          <div className="tg-card p-3">
            <div className="text-xs font-semibold mb-1">📦 Данные</div>
            <div className="grid grid-cols-3 text-center">
              <div><div className="text-lg font-bold">{user.data_counts.courses}</div><div className="text-[9px] text-[var(--tg-theme-hint-color)]">курсов</div></div>
              <div><div className="text-lg font-bold">{user.data_counts.students}</div><div className="text-[9px] text-[var(--tg-theme-hint-color)]">учеников</div></div>
              <div><div className="text-lg font-bold">{user.data_counts.groups}</div><div className="text-[9px] text-[var(--tg-theme-hint-color)]">групп</div></div>
            </div>
          </div>

          <div className="tg-card p-3">
            <div className="text-xs font-semibold mb-2">💳 Подписка</div>
            {user.subscription?.plan_id ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">Тариф: {user.subscription.plan_id}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border ${SUB_STATUS_STYLES[user.subscription.status || ''] || 'bg-gray-50 text-gray-600 border-gray-100'}`}>
                    {user.subscription.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-[var(--tg-theme-hint-color)]">Начало</span><span>{fmtDate(user.subscription.start_date || undefined)}</span>
                  <span className="text-[var(--tg-theme-hint-color)]">Окончание</span><span>{fmtDate(user.subscription.end_date || undefined)}</span>
                  <span className="text-[var(--tg-theme-hint-color)]">Payment ref</span><span className="truncate">{user.subscription.payment_reference || '—'}</span>
                </div>
                {user.subscription.history.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[var(--tg-theme-section-separator-color)]">
                    <div className="text-[10px] text-[var(--tg-theme-hint-color)] mb-1">История:</div>
                    {user.subscription.history.slice(0, 5).map((e, i) => (
                      <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
                        <span className="text-[var(--tg-theme-text-color)]">{e.event_type}</span>
                        <span className="text-[var(--tg-theme-hint-color)]">{fmtDateTime(e.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-[var(--tg-theme-hint-color)]">Нет активной подписки (Free)</p>
            )}
          </div>

          <div className="tg-card p-3">
            <div className="text-xs font-semibold mb-2">🎭 Роль</div>
            <div className="flex flex-wrap gap-1.5">
              {ROLE_OPTIONS.map(r => (
                <button
                  key={r}
                  disabled={busy}
                  onClick={() => onRoleChange(r)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-colors ${
                    r === user.role
                      ? 'bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] border-transparent'
                      : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)] border-[var(--tg-theme-section-separator-color)]'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={onBlock}
            disabled={busy}
            className={`w-full py-2.5 rounded-2xl text-sm font-semibold border transition-colors ${
              user.is_active
                ? 'bg-red-50 text-red-600 border-red-200'
                : 'bg-green-50 text-green-600 border-green-200'
            }`}
          >
            {user.is_active ? '🔒 Заблокировать' : '🔓 Разблокировать'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Schools ───────────────────────────────────────────────────────────────

function SchoolsTab() {
  const [schools, setSchools] = useState<OwnerSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<OwnerSchoolDetail | null>(null);

  useEffect(() => {
    api.getOwnerSchools()
      .then(data => { setSchools(data.schools); setError(null); })
      .catch(() => setError('Не удалось загрузить школы'))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = async (id: string) => {
    try {
      setSelected(await api.getOwnerSchool(id));
    } catch (e) {
      alert('Не удалось открыть школу');
    }
  };

  return (
    <>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {loading && <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}</div>}
      <div className="space-y-2">
        {schools.map(s => (
          <div key={s.id} className="tg-card p-3" onClick={() => openDetail(s.id)}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center text-base shrink-0">🏫</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-[10px] text-[var(--tg-theme-hint-color)] truncate">
                  {s.owner_name || 'без владельца'} · создана {fmtDate(s.created_at)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1 mt-2 text-center">
              <div><div className="text-sm font-bold">{s.members_total}</div><div className="text-[8px] text-[var(--tg-theme-hint-color)]">участ.</div></div>
              <div><div className="text-sm font-bold">{s.teachers_total}</div><div className="text-[8px] text-[var(--tg-theme-hint-color)]">учит.</div></div>
              <div><div className="text-sm font-bold">{s.students_total}</div><div className="text-[8px] text-[var(--tg-theme-hint-color)]">учеников</div></div>
              <div><div className="text-sm font-bold">{s.groups_total}</div><div className="text-[8px] text-[var(--tg-theme-hint-color)]">групп</div></div>
            </div>
          </div>
        ))}
        {!loading && schools.length === 0 && (
          <div className="tg-card py-6 text-center text-sm text-[var(--tg-theme-hint-color)]">Школ пока нет</div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setSelected(null)}>
          <div className="bg-[var(--tg-theme-bg-color)] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-semibold text-[var(--tg-theme-text-color)]">🏫 {selected.name}</div>
                <div className="text-xs text-[var(--tg-theme-hint-color)]">создана {fmtDate(selected.created_at)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[var(--tg-theme-hint-color)] text-xl leading-none">✕</button>
            </div>

            <div className="tg-card p-3 mb-3 text-xs">
              <div className="grid grid-cols-2 gap-y-1.5">
                <span className="text-[var(--tg-theme-hint-color)]">Владелец</span>
                <span>{selected.owner ? `${selected.owner.first_name} ${selected.owner.last_name}` : '—'}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Telegram owner</span><span>{selected.owner_telegram_id || '—'}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Timezone</span><span>{selected.timezone || '—'}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Приглашение</span>
                <span>{selected.has_invite ? `активно (${selected.invite_role || '?'})` : 'нет'}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              {[
                ['Участников', selected.members_total], ['Учителей', selected.teachers_total],
                ['Админов', selected.admins_total], ['Учеников', selected.students_total],
                ['Групп', selected.groups_total],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-xl bg-[var(--tg-theme-secondary-bg-color)] p-2">
                  <div className="text-lg font-bold">{value}</div>
                  <div className="text-[9px] text-[var(--tg-theme-hint-color)]">{label}</div>
                </div>
              ))}
            </div>

            <div className="text-xs font-semibold mb-2 text-[var(--tg-theme-text-color)]">👥 Команда ({selected.members.length})</div>
            <div className="space-y-1.5">
              {selected.members.map(m => (
                <div key={m.id} className="flex items-center gap-2.5 py-1">
                  <div className="w-7 h-7 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-[9px] font-bold shrink-0">
                    {m.first_name?.[0] || '?'}{m.last_name?.[0] || ''}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate">{m.first_name} {m.last_name}</div>
                    <div className="text-[9px] text-[var(--tg-theme-hint-color)]">{m.username ? `@${m.username}` : 'tg ' + m.telegram_id}{m.is_active ? '' : ' · 🔒'}</div>
                  </div>
                  <RoleBadge role={m.role} />
                </div>
              ))}
              {selected.members.length === 0 && (
                <div className="text-xs text-[var(--tg-theme-hint-color)] py-2">Команда пуста</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Subscriptions ─────────────────────────────────────────────────────────

function SubscriptionsTab() {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Subscription | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // форма активации (owner override)
  const [userIdInput, setUserIdInput] = useState('');
  const [planIdInput, setPlanIdInput] = useState('');
  const [showActivate, setShowActivate] = useState(false);
  // форма регистрации вебхука бота (Telegram Stars)
  const [showWebhook, setShowWebhook] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [webhookResult, setWebhookResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [subsData, plansData] = await Promise.all([api.getOwnerSubscriptions(), api.getOwnerPlans()]);
      setSubs(subsData.subscriptions);
      setPlans(plansData.plans);
      setError(null);
    } catch (e) {
      console.error('Failed to load subscriptions:', e);
      setError('Не удалось загрузить подписки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id: string) => {
    setBusyId(id);
    try {
      await api.cancelOwnerSubscription(id);
      await load();
    } catch (e) {
      alert('Не удалось отменить: ' + (e instanceof Error ? e.message : e));
    } finally {
      setBusyId(null);
    }
  };

  const openDetail = async (id: string) => {
    try {
      setSelected(await api.getOwnerSubscription(id));
    } catch (e) {
      alert('Не удалось открыть подписку');
    }
  };

  const activate = async () => {
    if (!userIdInput.trim() || !planIdInput) return;
    try {
      await api.createOwnerSubscription({ user_id: userIdInput.trim(), plan_id: planIdInput });
      setShowActivate(false);
      setUserIdInput('');
      await load();
    } catch (e) {
      alert('Не удалось активировать: ' + (e instanceof Error ? e.message : e));
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--tg-theme-hint-color)]">Всего: {subs.length}</p>
        <div className="flex gap-1.5">
          <button onClick={() => { setShowWebhook(open => { if (open) setWebhookUrl(''); else setWebhookUrl(TELEGRAM_WEBHOOK_URL); return !open; }); setShowActivate(false); }} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[var(--tg-theme-section-separator-color)] text-[var(--tg-theme-hint-color)]">
            🔗 Вебхук
          </button>
          <button onClick={() => { setShowActivate(v => !v); setShowWebhook(false); }} className="px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)]">
            + Активировать тариф
          </button>
        </div>
      </div>

      {showWebhook && (
        <div className="tg-card p-3 space-y-2">
          <p className="text-[10px] text-[var(--tg-theme-hint-color)]">
            Адрес бэкенда для уведомлений Telegram об оплате Stars:
          </p>
          <input
            value={webhookUrl}
            onChange={e => setWebhookUrl(e.target.value)}
            placeholder={TELEGRAM_WEBHOOK_URL}
            className="w-full rounded-xl px-3 py-2 text-sm bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]"
          />
          <button
            onClick={async () => {
              if (!webhookUrl.trim()) return;
              setWebhookBusy(true);
              setWebhookResult(null);
              try {
                const res = await api.registerTelegramWebhook(webhookUrl.trim());
                setWebhookResult(`✅ Вебхук установлен: ${res.status}`);
              } catch (e) {
                setWebhookResult('❌ Ошибка: ' + (e instanceof Error ? e.message : e));
              } finally {
                setWebhookBusy(false);
              }
            }}
            disabled={webhookBusy}
            className="w-full py-2 rounded-xl text-sm font-semibold bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] disabled:opacity-40"
          >
            {webhookBusy ? 'Устанавливаю…' : 'Установить вебхук'}
          </button>
          {webhookResult && <p className="text-[11px] text-[var(--tg-theme-text-color)] break-all">{webhookResult}</p>}
        </div>
      )}

      {showActivate && (
        <div className="tg-card p-3 space-y-2">
          <input
            value={userIdInput}
            onChange={e => setUserIdInput(e.target.value)}
            placeholder="user_id (usr_...)"
            className="w-full rounded-xl px-3 py-2 text-sm bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]"
          />
          <select
            value={planIdInput}
            onChange={e => setPlanIdInput(e.target.value)}
            className="w-full rounded-xl px-3 py-2 text-sm bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]"
          >
            <option value="">Тариф…</option>
            {plans.filter(p => p.is_active).map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.price_usd}$)</option>
            ))}
          </select>
          <button onClick={activate} className="w-full py-2 rounded-xl text-sm font-semibold bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)]">
            Активировать
          </button>
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
      {loading && <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}</div>}

      <div className="space-y-2">
        {subs.map(s => (
          <div key={s.id} className="tg-card p-3" onClick={() => openDetail(s.id)}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{s.user_name || s.user_id}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border ${SUB_STATUS_STYLES[s.status] || 'bg-gray-50 text-gray-600 border-gray-100'}`}>
                    {s.status}
                  </span>
                </div>
                <div className="text-[10px] text-[var(--tg-theme-hint-color)] truncate">
                  {s.plan_name || s.plan_id} · {fmtDate(s.start_date)} → {fmtDate(s.end_date)}
                  {s.payment_reference ? ` · ref ${s.payment_reference}` : ''}
                </div>
              </div>
              {s.status !== 'expired' && s.status !== 'cancelled' && (
                <button
                  onClick={e => { e.stopPropagation(); cancel(s.id); }}
                  disabled={busyId === s.id}
                  className="shrink-0 text-[10px] px-2 py-1 rounded-full border border-amber-200 text-amber-600 bg-amber-50"
                >
                  Отменить
                </button>
              )}
            </div>
          </div>
        ))}
        {!loading && subs.length === 0 && (
          <div className="tg-card py-6 text-center text-sm text-[var(--tg-theme-hint-color)]">Подписок пока нет</div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setSelected(null)}>
          <div className="bg-[var(--tg-theme-bg-color)] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-semibold">{selected.user_name || selected.user_id} — {selected.plan_name || selected.plan_id}</div>
                <div className={`text-[10px] px-2 py-0.5 rounded-full font-medium border inline-block mt-1 ${SUB_STATUS_STYLES[selected.status] || 'bg-gray-50 text-gray-600 border-gray-100'}`}>
                  {selected.status}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[var(--tg-theme-hint-color)] text-xl leading-none">✕</button>
            </div>

            <div className="tg-card p-3 mb-3 text-xs">
              <div className="grid grid-cols-2 gap-y-1.5">
                <span className="text-[var(--tg-theme-hint-color)]">Пользователь</span><span>{selected.user_name || selected.user_id}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Telegram</span><span>{selected.telegram_id || '—'}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Начало</span><span>{fmtDateTime(selected.start_date)}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Окончание</span><span>{fmtDateTime(selected.end_date)}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Trial до</span><span>{selected.trial_end ? fmtDate(selected.trial_end) : '—'}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Сумма</span><span>{selected.amount_paid} {selected.currency}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Payment ref</span><span className="break-all">{selected.payment_reference || '—'}</span>
                <span className="text-[var(--tg-theme-hint-color)]">Провайдер</span><span>{selected.payment_provider || '—'}</span>
              </div>
            </div>

            <div className="text-xs font-semibold mb-2">🕘 История</div>
            <div className="space-y-1">
              {(selected.history || []).map((e, i) => (
                <div key={e.id || i} className="flex items-center justify-between text-[11px] py-1">
                  <span className="text-[var(--tg-theme-text-color)]">{e.event_type}{e.detail ? ` — ${e.detail}` : ''}</span>
                  <span className="text-[var(--tg-theme-hint-color)] shrink-0 ml-2">{fmtDateTime(e.created_at)}</span>
                </div>
              ))}
              {(!selected.history || selected.history.length === 0) && (
                <div className="text-xs text-[var(--tg-theme-hint-color)]">Событий нет</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Plans (тарифы, редактируются owner) ──────────────────────────────────

const EMPTY_NEW_PLAN = { id: '', name: '', description: '', price_usd: '', price_stars: '', billing_period: 'monthly', trial_days: '', sort_order: '', is_active: true };

const EMPTY_NEW_DISCOUNT = { id: '', name: '', code: '', percent: '', plan_id: '', valid_from: '', valid_until: '', is_active: true };

function PlansTab() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [discounts, setDiscounts] = useState<OwnerDiscount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Plan>>({});
  // формы создания
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [newPlan, setNewPlan] = useState(EMPTY_NEW_PLAN);
  const [showNewDiscount, setShowNewDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState(EMPTY_NEW_DISCOUNT);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.getOwnerPlans(), api.getOwnerDiscounts()])
      .then(([plansData, discData]) => { setPlans(plansData.plans); setDiscounts(discData.discounts); setError(null); })
      .catch(() => setError('Не удалось загрузить тарифы'))
      .finally(() => setLoading(false));
  }, []);

  const draft = (id: string): Plan => drafts[id] || plans.find(p => p.id === id) || ({} as Plan);

  const patchDraft = (id: string, patch: Partial<Plan>) => {
    setDrafts(d => ({ ...d, [id]: { ...(d[id] || plans.find(p => p.id === id)), ...patch } }));
  };

  const save = async (id: string) => {
    const d = drafts[id];
    if (!d) return;
    setSavingId(id);
    try {
      const updated = await api.updateOwnerPlan(id, {
        name: d.name,
        description: d.description,
        price_usd: d.price_usd,
        price_stars: d.price_stars,
        billing_period: d.billing_period,
        trial_days: d.trial_days,
        sort_order: d.sort_order,
        is_active: d.is_active,
        features: d.features,
        limits: d.limits,
      });
      setPlans(ps => ps.map(p => p.id === id ? updated : p));
      setDrafts(dr => { const n = { ...dr }; delete n[id]; return n; });
    } catch (e) {
      alert('Не удалось сохранить: ' + (e instanceof Error ? e.message : e));
    } finally {
      setSavingId(null);
    }
  };

  const createPlan = async () => {
    const id = newPlan.id?.trim();
    if (!id) { alert('Укажите ID тарифа (например promo_pro)'); return; }
    const priceUsd = Number(newPlan.price_usd);
    if (isNaN(priceUsd) || priceUsd <= 0) { alert('Укажите цену в долларах (> 0)'); return; }
    const priceStars = Number(newPlan.price_stars);
    if (isNaN(priceStars) || priceStars <= 0) { alert('Укажите цену в ⭐ (> 0)'); return; }
    setBusy(true);
    try {
      const created = await api.createOwnerPlan({
        id,
        name: newPlan.name || id,
        description: newPlan.description,
        price_usd: String(priceUsd),
        price_stars: String(priceStars),
        billing_period: newPlan.billing_period || 'monthly',
        trial_days: newPlan.trial_days || '0',
        sort_order: newPlan.sort_order || '99',
        is_active: newPlan.is_active,
      });
      setPlans(ps => [...ps, created].sort((a, b) => (Number(a.sort_order) || 99) - (Number(b.sort_order) || 99)));
      setShowNewPlan(false);
      setNewPlan(EMPTY_NEW_PLAN);
    } catch (e) {
      alert('Не удалось создать тариф: ' + (e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  const createDiscount = async () => {
    const id = newDiscount.id?.trim();
    if (!id) { alert('Укажите ID скидки (например welcome20)'); return; }
    const percent = Number(newDiscount.percent);
    if (isNaN(percent) || percent <= 0 || percent > 100) { alert('Укажите процент скидки от 1 до 100'); return; }
    if (!newDiscount.valid_from || !newDiscount.valid_until) { alert('Укажите период действия скидки (от / до)'); return; }
    setBusy(true);
    try {
      const created = await api.createOwnerDiscount({
        id,
        name: newDiscount.name || id,
        code: newDiscount.code,
        percent,
        plan_id: newDiscount.plan_id,
        valid_from: newDiscount.valid_from,
        valid_until: newDiscount.valid_until,
        is_active: newDiscount.is_active,
      });
      setDiscounts(ds => [created, ...ds]);
      setShowNewDiscount(false);
      setNewDiscount(EMPTY_NEW_DISCOUNT);
    } catch (e) {
      alert('Не удалось создать скидку: ' + (e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  const toggleDiscount = async (d: OwnerDiscount) => {
    try {
      const updated = await api.updateOwnerDiscount(d.id, { is_active: !d.is_active });
      setDiscounts(ds => ds.map(x => x.id === d.id ? updated : x));
    } catch (e) {
      alert('Не удалось изменить скидку: ' + (e instanceof Error ? e.message : e));
    }
  };

  const deleteDiscount = async (d: OwnerDiscount) => {
    if (!confirm(`Удалить скидку «${d.name}»?`)) return;
    try {
      await api.deleteOwnerDiscount(d.id);
      setDiscounts(ds => ds.filter(x => x.id !== d.id));
    } catch (e) {
      alert('Не удалось удалить скидку: ' + (e instanceof Error ? e.message : e));
    }
  };

  if (loading) return <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}</div>;
  if (error) return <p className="text-xs text-red-500">{error}</p>;

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-[var(--tg-theme-hint-color)]">
        Цены и лимиты меняются здесь, без правок кода. Изменения применяются сразу.
      </p>

      {/* Actions: добавить тариф / скидку */}
      <div className="flex gap-1.5">
        <button
          onClick={() => { setShowNewPlan(v => !v); setShowNewDiscount(false); }}
          className="px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)]"
        >
          ➕ Добавить тариф
        </button>
        <button
          onClick={() => { setShowNewDiscount(v => !v); setShowNewPlan(false); }}
          className="px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)]"
        >
          🏷️ Добавить скидку
        </button>
      </div>

      {showNewPlan && (
        <div className="tg-card p-3 space-y-2">
          <div className="text-xs font-semibold text-[var(--tg-theme-text-color)]">Новый тариф</div>
          <div className="grid grid-cols-2 gap-2">
            <input value={newPlan.id} onChange={e => setNewPlan(p => ({ ...p, id: e.target.value }))} placeholder="ID (напр. promo_pro)" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
            <input value={newPlan.name} onChange={e => setNewPlan(p => ({ ...p, name: e.target.value }))} placeholder="Название" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
          </div>
          <input value={newPlan.description} onChange={e => setNewPlan(p => ({ ...p, description: e.target.value }))} placeholder="Описание" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
          <div className="grid grid-cols-3 gap-2">
            <input value={newPlan.price_usd} onChange={e => { const usd = e.target.value; const stars = usd ? String(Math.round(Number(usd) / 0.013)) : ''; setNewPlan(p => ({ ...p, price_usd: usd, price_stars: stars })); }} placeholder="Цена, $" type="number" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
            <input value={newPlan.price_stars} onChange={e => setNewPlan(p => ({ ...p, price_stars: e.target.value }))} placeholder="Цена, ⭐ (авто)" type="number" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
            <input value={newPlan.trial_days} onChange={e => setNewPlan(p => ({ ...p, trial_days: e.target.value }))} placeholder="Пробный период, дн" type="number" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={newPlan.billing_period} onChange={e => setNewPlan(p => ({ ...p, billing_period: e.target.value }))} className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]">
              <option value="monthly">Период: месяц</option>
              <option value="yearly">Период: год</option>
            </select>
            <input value={newPlan.sort_order} onChange={e => setNewPlan(p => ({ ...p, sort_order: e.target.value }))} placeholder="Порядок" type="number" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
          </div>
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] leading-snug">
            Период — как часто списывается оплата (месяц/год). Пробный период — бесплатные дни до первой оплаты (0 = без пробного). Цена в ⭐ подставляется из $ автоматически (1⭐ ≈ $0.013), её можно поправить вручную.
          </p>
          <button onClick={createPlan} disabled={busy} className="w-full py-2 rounded-xl text-xs font-semibold bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] disabled:opacity-40">
            {busy ? 'Создаю…' : 'Создать тариф'}
          </button>
        </div>
      )}

      {showNewDiscount && (
        <div className="tg-card p-3 space-y-2">
          <div className="text-xs font-semibold text-[var(--tg-theme-text-color)]">Новая скидка</div>
          <div className="grid grid-cols-2 gap-2">
            <input value={newDiscount.id} onChange={e => setNewDiscount(d => ({ ...d, id: e.target.value }))} placeholder="ID (напр. welcome20)" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
            <input value={newDiscount.name} onChange={e => setNewDiscount(d => ({ ...d, name: e.target.value }))} placeholder="Название" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={newDiscount.code} onChange={e => setNewDiscount(d => ({ ...d, code: e.target.value }))} placeholder="Промо-код (необяз.)" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
            <input value={newDiscount.percent} onChange={e => setNewDiscount(d => ({ ...d, percent: e.target.value }))} placeholder="Скидка, %" type="number" className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
          </div>
          <select value={newDiscount.plan_id} onChange={e => setNewDiscount(d => ({ ...d, plan_id: e.target.value }))} className="w-full rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]">
            <option value="">На все тарифы</option>
            {plans.filter(p => p.is_active).map(p => (
              <option key={p.id} value={p.id}>Только: {p.name}</option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] text-[var(--tg-theme-hint-color)]">Действует с
              <input value={newDiscount.valid_from} onChange={e => setNewDiscount(d => ({ ...d, valid_from: e.target.value }))} type="date" className="w-full mt-0.5 rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
            </label>
            <label className="text-[10px] text-[var(--tg-theme-hint-color)]">по
              <input value={newDiscount.valid_until} onChange={e => setNewDiscount(d => ({ ...d, valid_until: e.target.value }))} type="date" className="w-full mt-0.5 rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
            </label>
          </div>
          <button onClick={createDiscount} disabled={busy} className="w-full py-2 rounded-xl text-xs font-semibold bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] disabled:opacity-40">
            {busy ? 'Создаю…' : 'Создать скидку'}
          </button>
        </div>
      )}

      {plans.map(p => {
        const d = draft(p.id);
        return (
          <div key={p.id} className={`tg-card p-3 ${d.is_active === false ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold flex-1">{d.name || p.id} <span className="text-[10px] text-[var(--tg-theme-hint-color)] font-normal">({p.id})</span></span>
              <label className="flex items-center gap-1 text-[10px] text-[var(--tg-theme-hint-color)]">
                <input type="checkbox" checked={d.is_active !== false} onChange={e => patchDraft(p.id, { is_active: e.target.checked })} />
                вкл
              </label>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-2">
              <label className="text-[10px] text-[var(--tg-theme-hint-color)]">Цена, $
                <input value={d.price_usd} onChange={e => patchDraft(p.id, { price_usd: e.target.value })} className="w-full mt-0.5 rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
              </label>
              <label className="text-[10px] text-[var(--tg-theme-hint-color)]">Цена, ⭐ Stars
                <input value={d.price_stars} onChange={e => patchDraft(p.id, { price_stars: e.target.value })} className="w-full mt-0.5 rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
              </label>
              <label className="text-[10px] text-[var(--tg-theme-hint-color)]">Пробный период, дн (0 = нет)
                <input value={d.trial_days} onChange={e => patchDraft(p.id, { trial_days: e.target.value })} className="w-full mt-0.5 rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="text-[10px] text-[var(--tg-theme-hint-color)]">Период
                <select
                  value={d.billing_period || 'monthly'}
                  onChange={e => patchDraft(p.id, { billing_period: e.target.value })}
                  className="w-full mt-0.5 rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]"
                >
                  <option value="monthly">месяц</option>
                  <option value="yearly">год</option>
                </select>
              </label>
              <label className="text-[10px] text-[var(--tg-theme-hint-color)]">Порядок
                <input value={d.sort_order} onChange={e => patchDraft(p.id, { sort_order: e.target.value })} className="w-full mt-0.5 rounded-lg px-2 py-1.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]" />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-x-3 mb-2">
              <div>
                <div className="text-[10px] text-[var(--tg-theme-hint-color)] mb-1">Возможности</div>
                {Object.keys(FEATURE_LABELS).map(f => (
                  <label key={f} className="flex items-center gap-1.5 text-[11px] py-0.5 text-[var(--tg-theme-text-color)]">
                    <input type="checkbox" checked={!!d.features?.[f]} onChange={e => patchDraft(p.id, { features: { ...d.features, [f]: e.target.checked } })} />
                    {FEATURE_LABELS[f]}
                  </label>
                ))}
              </div>
              <div>
                <div className="text-[10px] text-[var(--tg-theme-hint-color)] mb-1">Лимиты</div>
                {Object.keys(LIMIT_LABELS).map(l => (
                  <label key={l} className="flex items-center justify-between gap-1.5 text-[11px] py-0.5 text-[var(--tg-theme-text-color)]">
                    <span>{LIMIT_LABELS[l]}</span>
                    <input
                      type="number"
                      value={d.limits?.[l] ?? 0}
                      onChange={e => patchDraft(p.id, { limits: { ...d.limits, [l]: Number(e.target.value) || 0 } })}
                      className="w-16 rounded-lg px-2 py-0.5 text-xs bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] outline-none border border-[var(--tg-theme-section-separator-color)]"
                    />
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={() => save(p.id)}
              disabled={savingId === p.id || !drafts[p.id]}
              className="w-full py-2 rounded-xl text-xs font-semibold bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] disabled:opacity-40"
            >
              {savingId === p.id ? 'Сохраняю…' : 'Сохранить изменения'}
            </button>
          </div>
        );
      })}

      {/* Скидки */}
      <div className="pt-1">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] flex items-center gap-2">
            <span>🏷️</span> Скидки
          </h3>
          <span className="text-[10px] text-[var(--tg-theme-hint-color)]">{discounts.length}</span>
        </div>

        {discounts.length === 0 && (
          <div className="tg-card p-4 text-center text-xs text-[var(--tg-theme-hint-color)]">
            Скидок пока нет — нажмите «🏷️ Добавить скидку»
          </div>
        )}

        <div className="space-y-2">
          {discounts.map(d => {
            const expired = d.valid_until && d.valid_until < new Date().toISOString().slice(0, 10);
            return (
              <div key={d.id} className={`tg-card p-3 ${!d.is_active || expired ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold flex-1">
                    {d.name} <span className="text-[var(--tg-theme-button-color)] font-bold">−{d.percent}%</span>
                    {d.code && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100 align-middle">{d.code}</span>}
                  </span>
                  <label className="flex items-center gap-1 text-[10px] text-[var(--tg-theme-hint-color)]">
                    <input type="checkbox" checked={d.is_active} onChange={() => toggleDiscount(d)} />
                    вкл
                  </label>
                </div>
                <div className="text-[10px] text-[var(--tg-theme-hint-color)] space-y-0.5">
                  <div>
                    {d.plan_id
                      ? <>На тариф: <span className="capitalize">{d.plan_id}</span></>
                      : 'На все тарифы'}
                  </div>
                  <div>
                    Период действия:{' '}
                    <span className="font-medium text-[var(--tg-theme-text-color)]">
                      {d.valid_from ? fmtDate(d.valid_from) : '∞'} → {d.valid_until ? fmtDate(d.valid_until) : '∞'}
                    </span>
                    {expired && <span className="ml-1 text-red-500">(истекла)</span>}
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={() => {
                      const percent = prompt('Процент скидки (1–100):', String(d.percent));
                      if (percent === null) return;
                      const p = Number(percent);
                      if (isNaN(p) || p <= 0 || p > 100) { alert('Процент от 1 до 100'); return; }
                      api.updateOwnerDiscount(d.id, { percent: p })
                        .then(updated => setDiscounts(ds => ds.map(x => x.id === d.id ? updated : x)))
                        .catch(e => alert('Не удалось изменить: ' + (e instanceof Error ? e.message : e)));
                    }}
                    className="text-[10px] px-2 py-1 rounded-full border border-[var(--tg-theme-section-separator-color)] text-[var(--tg-theme-hint-color)]"
                  >
                    ✏️ %
                  </button>
                  <button
                    onClick={() => {
                      const until = prompt('Действует до (ГГГГ-ММ-ДД):', d.valid_until || '');
                      if (until === null) return;
                      api.updateOwnerDiscount(d.id, { valid_until: until })
                        .then(updated => setDiscounts(ds => ds.map(x => x.id === d.id ? updated : x)))
                        .catch(e => alert('Не удалось изменить: ' + (e instanceof Error ? e.message : e)));
                    }}
                    className="text-[10px] px-2 py-1 rounded-full border border-[var(--tg-theme-section-separator-color)] text-[var(--tg-theme-hint-color)]"
                  >
                    📅 До
                  </button>
                  <button
                    onClick={() => deleteDiscount(d)}
                    className="text-[10px] px-2 py-1 rounded-full border border-red-200 text-red-500"
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
